import { useEffect, useRef, useCallback } from 'react';

interface TrailDot {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
}

interface BurstDot {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
  hue: number;
}

const MAX_TRAIL = 80;
const MAX_BURST = 20;
const TRAIL_SPAWN_MS = 30;
const HOVER_CHECK_INTERVAL = 100; // Throttle elementFromPoint to every 100ms

export default function CursorEffect() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const dotRef = useRef<HTMLDivElement>(null);
  const ringRef = useRef<HTMLDivElement>(null);
  const posRef = useRef({ x: -200, y: -200 });
  const prevPosRef = useRef({ x: -200, y: -200 });
  const ringPosRef = useRef({ x: -200, y: -200 });
  const rafRef = useRef<number>(0);
  const isVisibleRef = useRef(false);
  const trailRef = useRef<TrailDot[]>([]);
  const burstRef = useRef<BurstDot[]>([]);
  const trailTimerRef = useRef(0);
  const dprRef = useRef(1);
  const activeRef = useRef(false);
  const hoverElRef = useRef(false);
  const hoverCheckTimerRef = useRef(0);

  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    dprRef.current = dpr;
    const w = window.innerWidth;
    const h = window.innerHeight;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    const ctx = canvas.getContext('2d');
    if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }, []);

  const spawnClickBurst = useCallback((x: number, y: number) => {
    const burst = burstRef.current;
    const slots = MAX_BURST - burst.length;
    if (slots <= 0) return;

    const outer = Math.min(6, slots);
    for (let i = 0; i < outer; i++) {
      const angle = (Math.PI * 2 * i) / outer + (Math.random() - 0.5) * 0.5;
      const speed = 3 + Math.random() * 8;
      burst.push({
        x, y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: 1, maxLife: 0.4 + Math.random() * 0.6,
        size: 2 + Math.random() * 4,
        hue: 130 + Math.random() * 25,
      });
    }

    const innerSlots = MAX_BURST - burst.length;
    const inner = Math.min(4, innerSlots);
    for (let i = 0; i < inner; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 6 + Math.random() * 12;
      burst.push({
        x, y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: 1, maxLife: 0.2 + Math.random() * 0.4,
        size: 1 + Math.random() * 2.5,
        hue: 160 + Math.random() * 20,
      });
    }
  }, []);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    posRef.current = { x: e.clientX, y: e.clientY };
    if (!isVisibleRef.current) {
      isVisibleRef.current = true;
      if (dotRef.current) dotRef.current.style.opacity = '1';
      if (ringRef.current) ringRef.current.style.opacity = '1';
    }
  }, []);

  const handleMouseDown = useCallback((e: MouseEvent) => {
    if (e.button === 0) spawnClickBurst(e.clientX, e.clientY);
  }, [spawnClickBurst]);

  const handleMouseLeave = useCallback(() => {
    isVisibleRef.current = false;
    if (dotRef.current) dotRef.current.style.opacity = '0';
    if (ringRef.current) ringRef.current.style.opacity = '0';
    trailRef.current = [];
    burstRef.current = [];
  }, []);

  const handleMouseEnter = useCallback(() => {
    isVisibleRef.current = true;
    if (dotRef.current) dotRef.current.style.opacity = '1';
    if (ringRef.current) ringRef.current.style.opacity = '1';
  }, []);

  useEffect(() => {
    const isTouchDevice =
      'ontouchstart' in window ||
      navigator.maxTouchPoints > 0 ||
      window.matchMedia('(hover: none) and (pointer: coarse)').matches ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (isTouchDevice) return;
    activeRef.current = true;

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    document.documentElement.classList.add('custom-cursor-active');

    let lastTime = performance.now();

    const tick = (now: number) => {
      if (!activeRef.current) return;
      const dt = Math.min((now - lastTime) / 1000, 0.1);
      lastTime = now;

      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      const dot = dotRef.current;
      const ring = ringRef.current;
      const tx = posRef.current.x;
      const ty = posRef.current.y;

      // --- Dot: instant ---
      if (dot && isVisibleRef.current) {
        dot.style.transform = `translate3d(${tx}px, ${ty}px, 0)`;
      }

      // --- Ring: smooth spring follow ---
      if (ring) {
        ringPosRef.current.x += (tx - ringPosRef.current.x) * 0.14;
        ringPosRef.current.y += (ty - ringPosRef.current.y) * 0.14;
        ring.style.transform = `translate3d(${ringPosRef.current.x}px, ${ringPosRef.current.y}px, 0)`;
      }

      // --- Hover detection: THROTTLED to save performance ---
      hoverCheckTimerRef.current += dt * 1000;
      if (hoverCheckTimerRef.current >= HOVER_CHECK_INTERVAL) {
        hoverCheckTimerRef.current -= HOVER_CHECK_INTERVAL;
        const elUnder = document.elementFromPoint(tx, ty);
        const isInteractive = !!(
          elUnder?.closest('a, button, input, textarea, select, [role="button"], [onclick], .clickable, [data-cursor-pointer]')
        );
        if (isInteractive !== hoverElRef.current) {
          hoverElRef.current = isInteractive;
          if (isInteractive) {
            if (dot) { dot.style.width = '24px'; dot.style.height = '24px'; dot.style.marginLeft = '-12px'; dot.style.marginTop = '-12px'; dot.style.opacity = '0.3'; dot.style.backgroundColor = 'oklch(var(--primary-500))'; }
            if (ring) { ring.style.width = '96px'; ring.style.height = '96px'; ring.style.marginLeft = '-48px'; ring.style.marginTop = '-48px'; ring.style.borderWidth = '3px'; ring.style.borderColor = 'oklch(var(--primary-500) / 0.45)'; ring.style.opacity = '0.8'; }
          } else {
            if (dot) { dot.style.width = '8px'; dot.style.height = '8px'; dot.style.marginLeft = '-4px'; dot.style.marginTop = '-4px'; dot.style.opacity = '1'; dot.style.backgroundColor = 'oklch(var(--accent-500))'; }
            if (ring) { ring.style.width = '32px'; ring.style.height = '32px'; ring.style.marginLeft = '-16px'; ring.style.marginTop = '-16px'; ring.style.borderWidth = '3px'; ring.style.borderColor = 'oklch(var(--accent-500) / 0.55)'; ring.style.opacity = '1'; }
          }
        }
      }

      // --- Spawn trail dots ---
      if (isVisibleRef.current) {
        trailTimerRef.current += dt * 1000;
        if (trailTimerRef.current >= TRAIL_SPAWN_MS) {
          trailTimerRef.current -= TRAIL_SPAWN_MS;
          const px = prevPosRef.current.x;
          const py = prevPosRef.current.y;
          const dist = Math.hypot(tx - px, ty - py);
          const count = Math.min(Math.floor(dist / 5) + 1, 4);
          const trail = trailRef.current;
          for (let i = 0; i < count; i++) {
            if (trail.length >= MAX_TRAIL) break;
            const t = i / Math.max(count, 1);
            trail.push({
              x: px + (tx - px) * t + (Math.random() - 0.5) * 4,
              y: py + (ty - py) * t + (Math.random() - 0.5) * 4,
              vx: (Math.random() - 0.5) * 0.8,
              vy: (Math.random() - 0.5) * 0.8 - 0.2,
              life: 1, maxLife: 0.3 + Math.random() * 0.5,
              size: 1.5 + Math.random() * 3,
            });
          }
        }
      }
      prevPosRef.current = { x: tx, y: ty };

      // --- Draw everything ---
      if (ctx && canvas) {
        const w = canvas.width / dprRef.current;
        const h = canvas.height / dprRef.current;
        ctx.clearRect(0, 0, w, h);

        const trail = trailRef.current;
        for (let i = trail.length - 1; i >= 0; i--) {
          const p = trail[i];
          p.life -= dt / p.maxLife;
          if (p.life <= 0) { trail.splice(i, 1); continue; }
          p.x += p.vx;
          p.y += p.vy;
          const a = p.life * 0.55;
          const r = p.size * p.life;
          ctx.beginPath();
          ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
          ctx.fillStyle = `hsla(145, 55%, 52%, ${a})`;
          ctx.fill();
        }

        const burst = burstRef.current;
        for (let i = burst.length - 1; i >= 0; i--) {
          const b = burst[i];
          b.life -= dt / b.maxLife;
          if (b.life <= 0) { burst.splice(i, 1); continue; }
          b.x += b.vx;
          b.y += b.vy;
          b.vx *= 0.94;
          b.vy *= 0.94;
          b.vy += 0.45;
          const a = b.life * 0.7;
          const r = b.size * b.life;
          ctx.beginPath();
          ctx.arc(b.x, b.y, r, 0, Math.PI * 2);
          ctx.fillStyle = `hsla(${b.hue}, 60%, 52%, ${a})`;
          ctx.fill();
        }
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    window.addEventListener('mousedown', handleMouseDown, { passive: true });
    document.addEventListener('mouseleave', handleMouseLeave);
    document.addEventListener('mouseenter', handleMouseEnter);

    return () => {
      activeRef.current = false;
      cancelAnimationFrame(rafRef.current);
      document.documentElement.classList.remove('custom-cursor-active');
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('mouseenter', handleMouseEnter);
      window.removeEventListener('resize', resizeCanvas);
    };
  }, [resizeCanvas, handleMouseMove, handleMouseDown, handleMouseLeave, handleMouseEnter]);

  return (
    <>
      <canvas
        ref={canvasRef}
        className="fixed inset-0 pointer-events-none"
        style={{ zIndex: 9998 }}
      />
      <div
        ref={ringRef}
        id="custom-cursor-ring"
        className="fixed pointer-events-none w-8 h-8 -ml-4 -mt-4 rounded-full border-[3px] border-accent-500/55 transition-[width,height,margin,border-color,background,border-width] duration-200 ease-out"
        style={{ willChange: 'transform', opacity: 0, zIndex: 9999 }}
      />
      <div
        ref={dotRef}
        id="custom-cursor-dot"
        className="fixed pointer-events-none w-2 h-2 -ml-1 -mt-1 rounded-full bg-accent-500 transition-transform duration-100 ease-out"
        style={{ willChange: 'transform', opacity: 0, zIndex: 9999 }}
      />
    </>
  );
}